import React, { useEffect, useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Building,
  Meter,
  EnergyReadingsResponse,
  PvReadingsResponse,
  TimeFilter,
} from '../types';
import buildingService from '../services/buildingService';
import energyService from '../services/energyService';
import ExpandableMetricCard, { ChartSegment } from '../components/dashboard/SummaryCard';
import TimeFilterComponent from '../components/dashboard/TimeFilter';
import { useMeterPhotos } from '../components/dashboard/MeterPhotos';
import { fillReadingTimeSeries, fillPvTimeSeries } from '../utils/fillTimeSeries';

function sumReadings(response: EnergyReadingsResponse | undefined): number | null {
  if (!response || response.data.length === 0) return null;
  return response.data.reduce((acc, d) => acc + d.value, 0);
}

function sumPv(response: PvReadingsResponse | undefined, field: 'productionKwh' | 'feedInKwh' | 'selfConsumptionKwh'): number | null {
  if (!response || response.data.length === 0) return null;
  return response.data.reduce((acc, d) => acc + d[field], 0);
}

// Farben der Balkenanteile. Der Strom aus der PV-Anlage hat in beiden Kacheln
// dieselbe Farbe, damit man ihn wiedererkennt.
const SEGMENT_COLORS = {
  grid: '#2b8fd0',
  pvOwnUse: '#d97706',
  feedIn: '#0d9488',
  unknown: '#94a3b8',
};

const Dashboard: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [activeFilter, setActiveFilter] = useState<TimeFilter>('THIS_MONTH');

  const [stromData, setStromData] = useState<EnergyReadingsResponse | undefined>();
  const [gasData, setGasData] = useState<EnergyReadingsResponse | undefined>();
  const [wasserData, setWasserData] = useState<EnergyReadingsResponse | undefined>();
  const [pvData, setPvData] = useState<PvReadingsResponse | undefined>();

  const [loadingBuildings, setLoadingBuildings] = useState(true);
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [buildingDropdownOpen, setBuildingDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setBuildingDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    buildingService.getAll()
      .then(data => {
        const inBetrieb = data.filter(b => b.status === 'IN_BETRIEB');
        setBuildings(inBetrieb);
        // Vorausgewählt ist das Gebäude mit den meisten Zählern — dort ist am
        // meisten zu sehen. Bei Gleichstand gewinnt das erste der nach Namen
        // sortierten Liste, damit die Auswahl zwischen zwei Aufrufen dieselbe
        // bleibt. `meterCount` zählt nur aktive Zähler (BuildingRepository).
        const withMostMeters = inBetrieb.reduce<Building | null>(
          (best, b) => (best === null || b.meterCount > best.meterCount ? b : best),
          null
        );
        if (withMostMeters) setSelectedBuildingId(withMostMeters.id);
      })
      .catch(() => toast.error('Fehler beim Laden der Gebäude'))
      .finally(() => setLoadingBuildings(false));
  }, []);

  useEffect(() => {
    if (!selectedBuildingId) return;
    // Zähler und Messwerte des zuvor gewählten Gebäudes verwerfen. Ohne das
    // blieben Kacheln stehen, für die das neue Gebäude gar keinen Zähler hat —
    // mit den Zahlen des alten Gebäudes darin.
    setMeters([]);
    setStromData(undefined);
    setGasData(undefined);
    setWasserData(undefined);
    setPvData(undefined);
    setLoadingMeters(true);
    buildingService.getMeters(selectedBuildingId)
      .then(setMeters)
      .catch(() => toast.error('Fehler beim Laden der Zähler'))
      .finally(() => setLoadingMeters(false));
  }, [selectedBuildingId]);

  const loadEnergyData = useCallback(async () => {
    const stromMeter  = meters.find(m => m.type === 'STROM');
    const gasMeter    = meters.find(m => m.type === 'GAS');
    const wasserMeter = meters.find(m => m.type === 'WASSER');
    const pvMeter     = meters.find(m => m.type === 'PV');

    // Kein Zähler dieses Typs heißt: keine Werte. Sonst bliebe der Stand des
    // vorherigen Gebäudes im State liegen.
    if (!stromMeter) setStromData(undefined);
    if (!gasMeter) setGasData(undefined);
    if (!wasserMeter) setWasserData(undefined);
    if (!pvMeter) setPvData(undefined);

    if (meters.length === 0) return;
    setLoadingData(true);

    try {
      await Promise.all([
        stromMeter ? energyService.getReadings(stromMeter.id, activeFilter).then(setStromData) : Promise.resolve(),
        gasMeter ? energyService.getReadings(gasMeter.id, activeFilter).then(setGasData) : Promise.resolve(),
        wasserMeter ? energyService.getReadings(wasserMeter.id, activeFilter).then(setWasserData) : Promise.resolve(),
        pvMeter ? energyService.getPvReadings(pvMeter.id, activeFilter).then(setPvData) : Promise.resolve(),
      ]);
    } catch {
      toast.error('Fehler beim Laden der Messwerte');
    } finally {
      setLoadingData(false);
    }
  }, [meters, activeFilter]);

  useEffect(() => {
    loadEnergyData();
  }, [loadEnergyData]);

  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);

  const stromMeterObj  = meters.find(m => m.type === 'STROM');
  const gasMeterObj    = meters.find(m => m.type === 'GAS');
  const wasserMeterObj = meters.find(m => m.type === 'WASSER');
  const pvMeterObj     = meters.find(m => m.type === 'PV');

  // Zählerfotos gehören zu ihrem Zähler: der Knopf sitzt im Kopf der Kachel,
  // die Fotos liegen in ihrem aufgeklappten Bereich. Zähler ohne Kamera
  // bekommen beides nicht.
  const photos = useMeterPhotos(meters);

  // Fill time series gaps so every period is shown on the chart
  const filledStromData = fillReadingTimeSeries(stromData?.data || [], activeFilter);
  const filledGasData = fillReadingTimeSeries(gasData?.data || [], activeFilter);
  const filledWasserData = fillReadingTimeSeries(wasserData?.data || [], activeFilter);
  const filledPvRaw = fillPvTimeSeries(pvData?.data || [], activeFilter);

  // Seit ein Netzzähler seine Einspeisung auf den PV-Zähler buchen kann, stehen
  // Erzeugung und Einspeisung in verschiedenen Zeilen. Der Eigenverbrauch ist
  // deshalb die Differenz der Summen über den Zeitraum, nicht die Summe der
  // Differenzen je Zeile.
  const pvProduction = sumPv(pvData, 'productionKwh');
  const pvFeedIn = sumPv(pvData, 'feedInKwh');
  const pvSelfConsumption =
    pvProduction !== null && pvFeedIn !== null ? Math.max(0, pvProduction - pvFeedIn) : null;

  // Eigenverbrauch lässt sich nur ausweisen, wenn über den ganzen Zeitraum
  // eine Einspeisungsmessung vorlag. Davor ist „Erzeugung - Einspeisung" die
  // Erzeugung, und die Kachel behauptete, alles sei selbst verbraucht worden.
  const pvFeedInSince = pvData?.feedInSince ? new Date(pvData.feedInSince) : null;
  const pvPeriodStart = filledPvRaw.length > 0 ? new Date(filledPvRaw[0].period) : null;
  const pvFeedInCoversPeriod =
    pvFeedInSince !== null && pvPeriodStart !== null && pvFeedInSince <= pvPeriodStart;
  const pvBreakdownNote = pvFeedInCoversPeriod
    ? undefined
    : pvFeedInSince === null
      ? 'Für diesen Zähler wird noch keine Einspeisung gemessen.'
      : `Einspeisung wird erst seit dem ${pvFeedInSince.toLocaleDateString('de-DE')} gemessen — für diesen Zeitraum nicht ausweisbar.`;

  // Dieselbe Regel je Balken: ein Tag (eine Stunde, ein Monat) lässt sich nur
  // aufteilen, wenn die Einspeisung ab seinem Beginn gemessen wurde.
  const pvSplittable = (period: string) =>
    pvFeedInSince !== null && pvFeedInSince <= new Date(period);

  const pvChartData = filledPvRaw.map(d => {
    const split = pvSplittable(d.period);
    return {
      period: d.period,
      value: d.productionKwh,
      ownUse: split ? d.selfConsumptionKwh : null,
      feedIn: split ? d.feedInKwh : null,
      unknown: split ? null : d.productionKwh,
    };
  });
  const pvSegments: ChartSegment[] = [
    { key: 'ownUse', label: 'Eigenverbrauch', color: SEGMENT_COLORS.pvOwnUse },
    { key: 'feedIn', label: 'Eingespeist', color: SEGMENT_COLORS.feedIn },
    // Nur zeigen, wenn es im Zeitraum Erzeugung ohne Einspeisungsmessung gab.
    ...(pvChartData.some(d => (d.unknown ?? 0) > 0)
      ? [{ key: 'unknown', label: 'Nicht aufteilbar', color: SEGMENT_COLORS.unknown }]
      : []),
  ];

  // Stromverbrauch = Netzbezug + selbst verbrauchter PV-Strom. Der Stromzähler
  // misst nur den Bezug; den Teil aus der PV-Anlage liefert der PV-Zähler.
  // Beide Reihen kommen aus derselben Periodenliste, die Indizes passen.
  const stromGrid = sumReadings(stromData);
  const stromChartData = filledStromData.map((d, i) => {
    const pv = pvMeterObj ? filledPvRaw[i] : undefined;
    const fromPv = pv && pvSplittable(pv.period) ? pv.selfConsumptionKwh : null;
    return { period: d.period, value: d.value + (fromPv ?? 0), grid: d.value, fromPv };
  });
  const stromSegments: ChartSegment[] | undefined = pvMeterObj
    ? [
        { key: 'grid', label: 'Vom Netz', color: SEGMENT_COLORS.grid },
        { key: 'fromPv', label: 'Aus PV', color: SEGMENT_COLORS.pvOwnUse },
      ]
    : undefined;
  // Die grosse Zahl ist nur dann der Gesamtverbrauch, wenn der PV-Anteil über
  // den ganzen Zeitraum bekannt ist. Sonst bleibt sie beim Netzbezug.
  const stromShowsTotal = !!pvMeterObj && pvFeedInCoversPeriod && pvSelfConsumption !== null;
  const stromValue =
    stromGrid !== null && stromShowsTotal ? stromGrid + pvSelfConsumption! : stromGrid;
  const stromBreakdownNote = !pvMeterObj || stromShowsTotal || stromGrid === null
    ? undefined
    : pvFeedInSince === null
      ? 'Ohne Einspeisungsmessung ist der Anteil aus der PV-Anlage nicht bekannt — gezeigt ist nur der Netzbezug.'
      : `Der Anteil aus der PV-Anlage ist erst ab dem ${pvFeedInSince.toLocaleDateString('de-DE')} bekannt — gezeigt ist nur der Netzbezug.`;

  return (
    <div className="space-y-10 animate-in fade-in duration-700">
      
      {/* Header Area */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">

        {/* Links: Titel + Gebäude-Auswahl */}
        <div className="space-y-3">

          {/* Gebäude-Dropdown prominent unter dem Titel */}
          {!loadingBuildings && buildings.length > 0 && (
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setBuildingDropdownOpen(prev => !prev)}
                className="flex items-center gap-4 px-5 py-4 glass-card !rounded-2xl cursor-pointer hover:shadow-lg transition-all duration-200 group"
              >
                <div className="w-10 h-10 rounded-xl bg-[#216dab]/10 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-[#216dab]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-sm font-medium text-slate-400">Gebäude</span>
                  <span className="text-[#216dab] dark:text-[#3aa6e9] font-semibold text-lg leading-tight">
                    {selectedBuilding?.name ?? '—'}
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-slate-400 ml-1 transition-transform duration-200 ${buildingDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {buildingDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 min-w-full glass-card !rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150 py-1">
                  {buildings.map(b => (
                    <button
                      key={b.id}
                      onClick={() => { setSelectedBuildingId(b.id); setBuildingDropdownOpen(false); }}
                      className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors
                        ${b.id === selectedBuildingId
                          ? 'bg-[#216dab]/10 text-[#216dab] dark:text-[#3aa6e9]'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/5'
                        }`}
                    >
                      <span className="font-bold text-sm">{b.name}</span>
                      {b.id === selectedBuildingId && (
                        <svg className="w-4 h-4 ml-auto shrink-0 text-[#216dab]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rechts: Zeitfilter */}
        <div className="flex items-center gap-2 bg-black/5 dark:bg-white/5 p-2 rounded-3xl border border-black/5 dark:border-white/5">
          <TimeFilterComponent active={activeFilter} onChange={setActiveFilter} />
        </div>
      </div>

      {/* Main Grid: Consumption Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 pb-20">
        {stromMeterObj && (
          <ExpandableMetricCard
            title="Stromverbrauch"
            seriesLabel={stromSegments ? 'Verbrauch' : 'Bezug'}
            value={stromValue}
            breakdown={stromSegments && stromGrid !== null ? [
              { label: 'Vom Netz', value: stromGrid },
              { label: 'Aus PV', value: stromShowsTotal ? pvSelfConsumption : null },
            ] : undefined}
            breakdownNote={stromBreakdownNote}
            segments={stromSegments}
            unit="kWh"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
            color="indigo"
            data={stromChartData}
            filter={activeFilter}
            loading={loadingData}
            currentReading={stromMeterObj.currentReading}
            headerAction={photos.actionFor(stromMeterObj)}
            expandedExtra={photos.listFor(stromMeterObj)}
          />
        )}

        {pvMeterObj && (
          <ExpandableMetricCard
            title="PV Erzeugung"
            value={pvProduction}
            seriesLabel="Erzeugung"
            breakdown={pvProduction === null ? undefined : [
              { label: 'Eingespeist', value: pvFeedInCoversPeriod ? pvFeedIn : null },
              { label: 'Eigenverbrauch', value: pvFeedInCoversPeriod ? pvSelfConsumption : null },
            ]}
            breakdownNote={pvProduction === null ? undefined : pvBreakdownNote}
            unit="kWh"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 9H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>}
            color="amber"
            data={pvChartData}
            segments={pvProduction === null ? undefined : pvSegments}
            filter={activeFilter}
            loading={loadingData}
            headerAction={photos.actionFor(pvMeterObj)}
            expandedExtra={photos.listFor(pvMeterObj)}
          />
        )}

        {gasMeterObj && (
          <ExpandableMetricCard
            title="Gasverbrauch"
            seriesLabel="Verbrauch"
            value={sumReadings(gasData)}
            unit="m³"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.99 7.99 0 0120 13a7.99 7.99 0 01-2.343 5.657z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14l-2.121 2.121z" /></svg>}
            color="orange"
            data={filledGasData}
            filter={activeFilter}
            loading={loadingData}
            currentReading={gasMeterObj.currentReading}
            headerAction={photos.actionFor(gasMeterObj)}
            expandedExtra={photos.listFor(gasMeterObj)}
          />
        )}

        {wasserMeterObj && (
          <ExpandableMetricCard
            title="Wasserverbrauch"
            seriesLabel="Verbrauch"
            value={sumReadings(wasserData)}
            unit="m³"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13c0 5-3.5 7.5-7.667 7.5S4.667 18 4.667 13C4.667 8.5 8.5 3 12.333 3s7.667 5.5 7.667 10z" /></svg>}
            color="cyan"
            data={filledWasserData}
            filter={activeFilter}
            loading={loadingData}
            currentReading={wasserMeterObj.currentReading}
            headerAction={photos.actionFor(wasserMeterObj)}
            expandedExtra={photos.listFor(wasserMeterObj)}
          />
        )}
      </div>

      {!loadingMeters && selectedBuildingId !== null && meters.length === 0 && (
        <div className="text-center py-10 glass-card">
          <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">
            Für dieses Gebäude sind keine Zähler hinterlegt.
          </p>
        </div>
      )}

      {photos.previewModal}
    </div>
  );
};

export default Dashboard;
